/* istanbul ignore file */
import { useQuery } from '@tanstack/react-query';
import { rootApi } from 'app/api/base';
import type React from 'react';
import { createContext, useContext } from 'react';
import useAccount from './account';

interface HubspotCompany {
  id: string;
  state: string;
  country: string;
  company_size: number;
  created_at: string;
}

interface HubspotMeeting {
  id: string;
  title: string;
  start_time: string;
}

interface HubspotPipeline {
  id: string;
  label: string;
}

interface HubspotDeal {
  id: string;
  name: string;
  dealstage: string;
  pipeline: HubspotPipeline;
}

interface HubspotData {
  company: HubspotCompany | null;
  latestMeeting: HubspotMeeting | null;
  latestDeal: HubspotDeal | null;
}

interface HubspotContextType {
  isLoading: boolean;
  error: Error | null;
  hubspotData: HubspotData;
}

const defaultValues: HubspotContextType = {
  isLoading: false,
  error: null,
  hubspotData: null,
};

const HubspotContext = createContext<HubspotContextType>(defaultValues);

const HubspotProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentCompany } = useAccount();

  const {
    data: hubspotData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['trial-data'],
    queryFn: async () => {
      const { data } = await rootApi('trial/v1/company').get({});
      return data;
    },
    enabled: currentCompany?.billing_type === 'automated_trial',
  });

  return (
    <HubspotContext.Provider
      value={{
        isLoading,
        error: error as Error | null,
        hubspotData,
      }}
    >
      {children}
    </HubspotContext.Provider>
  );
};

const useHubspot = () => useContext(HubspotContext);

export { HubspotContext, HubspotProvider, useHubspot };
